import { Checkbox } from '@HoudiniUI/Checkbox';
import { FieldError } from '@HoudiniUI/Field';
import { DateInput } from '@HoudiniUI/DateInput';
import { Input } from '@HoudiniUI/Input';
import { Label } from '@HoudiniUI/Label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import { TextArea } from '@HoudiniUI/TextArea';
import { IRegEdMetadataField, IRegEdMetadataForm, RegEdFieldType } from '@Queries/RegEdCompliance/types';
import { css } from 'aphrodite';
import React from 'react';
import { useConditionalVisibility } from '../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import {
	findReviewCompleteAnswer,
	isUnpublishableToOption,
	normalizeFieldType,
	orderedActiveFields,
	regEdOptionValue,
	findReviewFieldId,
	resolveFirstUseFieldId,
	toFormValues,
	toSurveyForm,
} from './regEdMetadataFormAdapter';
import { styleSheet } from './styles';

export interface RegEdMetadataFormProps {
	schema: IRegEdMetadataForm | null | undefined;
	/** Answer bag keyed by field id (matches the API's `RegEdMetadataAnswers` dictionary). */
	answers: Record<string, unknown>;
	onChange: (answers: Record<string, unknown>) => void;
	disabled?: boolean;
	/** Required fields left blank at the last submit attempt; marked in place until answered. */
	invalidFieldIds?: ReadonlySet<string>;
	/**
	 * `Date.getDay()` numbers the date of first use cannot land on, because the channel this is being
	 * filed for does not send those days. Applies to that question only.
	 */
	firstUseUnavailableDaysOfWeek?: number[];
	/** Questions the host derives on the advisor's behalf, each with the note that says where it comes from. */
	lockedFieldNotes?: ReadonlyMap<string, string>;
	/** `yyyy-MM-dd` the review has to be complete by — the send, which it clears. Applies to that question only. */
	reviewDeadline?: string;
}

const inputTypeForField = (fieldType: RegEdFieldType | undefined): string => {
	switch (fieldType) {
		case RegEdFieldType.Email:
			return 'email';
		case RegEdFieldType.Integer:
		case RegEdFieldType.Decimal:
		case RegEdFieldType.Money:
			return 'number';
		default:
			return 'text';
	}
};

/** Required markers are display-only — validation is enforced on the submit path. */
export const RegEdMetadataForm: React.FC<RegEdMetadataFormProps> = ({
	schema,
	answers,
	onChange,
	disabled,
	invalidFieldIds,
	firstUseUnavailableDaysOfWeek,
	lockedFieldNotes,
	reviewDeadline,
}) => {
	const fields = React.useMemo(() => orderedActiveFields(schema), [schema]);
	const surveyForm = React.useMemo(() => toSurveyForm(fields), [fields]);
	const formValues = React.useMemo(() => toFormValues(answers), [answers]);
	const reviewCompleteDate = React.useMemo(() => findReviewCompleteAnswer(schema, answers), [schema, answers]);
	const firstUseFieldId = React.useMemo(() => resolveFirstUseFieldId(schema, answers), [schema, answers]);
	const reviewFieldId = React.useMemo(() => findReviewFieldId(schema), [schema]);
	const visibleFieldIds = useConditionalVisibility(surveyForm, formValues);

	const setAnswer = (fieldId: string, value: unknown) => {
		onChange({ ...answers, [fieldId]: value });
	};

	const toggleMultiOption = (fieldId: string, externalId: string, checked: boolean) => {
		const current = Array.isArray(answers[fieldId]) ? (answers[fieldId] as string[]) : [];
		const next = checked ? [...current, externalId] : current.filter(v => v !== externalId);
		setAnswer(fieldId, next);
	};

	const renderWidget = (field: IRegEdMetadataField, fieldType: RegEdFieldType | undefined) => {
		const fieldId = field.id!;
		const rawValue = answers[fieldId];

		switch (fieldType) {
			case RegEdFieldType.Text:
				return (
					<TextArea
						id={fieldId}
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={e => setAnswer(fieldId, e.target.value)}
						disabled={disabled}
						rows={4}
					/>
				);
			case RegEdFieldType.Boolean:
				return (
					<Checkbox
						checked={rawValue === true}
						onCheckedChange={checked => setAnswer(fieldId, checked === true)}
						disabled={disabled}
					/>
				);
			case RegEdFieldType.Option: {
				const current = typeof rawValue === 'string' ? rawValue : '';
				const selected = field.options?.find(o => regEdOptionValue(o) === current);
				return (
					<Select value={current} onValueChange={value => setAnswer(fieldId, value)} disabled={disabled}>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{selected?.label ?? 'Select…'}</span>
						</SelectTrigger>
						<SelectContent>
							{(field.options ?? [])
								.filter(o => o.isActive !== false && !!regEdOptionValue(o))
								.map((o, i) => (
									<SelectItem
										key={`${regEdOptionValue(o)}-${i}`}
										value={regEdOptionValue(o)}
										disabled={isUnpublishableToOption(o.label) && regEdOptionValue(o) !== current}
									>
										{o.label}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				);
			}
			case RegEdFieldType.MultipleOptions: {
				const current = Array.isArray(rawValue) ? (rawValue as string[]) : [];
				return (
					<div className={css(styleSheet.optionsGroup)}>
						{(field.options ?? [])
							.filter(o => o.isActive !== false && !!regEdOptionValue(o))
							.map((o, i) => {
								const value = regEdOptionValue(o);
								const checked = current.includes(value);
								return (
									<label key={`${value}-${i}`} className={css(styleSheet.optionRow)}>
										<Checkbox
											checked={checked}
											onCheckedChange={next => toggleMultiOption(fieldId, value, next === true)}
											// Still clearable when a prior filing asserted it, so a revision is never stuck with it.
											disabled={disabled || (isUnpublishableToOption(o.label) && !checked)}
										/>
										<span>{o.label}</span>
									</label>
								);
							})}
					</div>
				);
			}
			case RegEdFieldType.Date:
				return (
					<DateInput
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={value => setAnswer(fieldId, value)}
						disabled={disabled || !!lockedFieldNotes?.has(fieldId)}
						minimumDate={field.minimumDate ?? undefined}
						minimumDateLabel={date => `Your firm asks for ${date} or later`}
						// Material cannot go into use before the review clearing it has finished. Bounded on
						// that question alone: other date questions can legitimately predate review.
						earliestSelectableDate={fieldId === firstUseFieldId ? (reviewCompleteDate ?? undefined) : undefined}
						// Bounded on the same question alone: first use is the send, so it has to land on a
						// day the channel can actually send. The other date questions are not sends.
						unavailableDaysOfWeek={fieldId === firstUseFieldId ? firstUseUnavailableDaysOfWeek : undefined}
						latestSelectableDate={fieldId === reviewFieldId ? reviewDeadline : undefined}
						latestSelectableDateLabel={date => `Must be complete by the send date, ${date}`}
					/>
				);
			default:
				return (
					<Input
						id={fieldId}
						type={inputTypeForField(fieldType)}
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={e => setAnswer(fieldId, e.target.value)}
						disabled={disabled}
					/>
				);
		}
	};

	return (
		<div className={css(styleSheet.container)}>
			{fields.map(field => {
				const fieldId = field.id!;
				if (visibleFieldIds && !visibleFieldIds.has(fieldId)) {
					return null;
				}
				const fieldType = normalizeFieldType(field.fieldType);
				// Only Input/TextArea widgets render an element with `id={fieldId}`; composite controls
				// (Select/Checkbox/MultipleOptions/DateInput) don't, so `htmlFor` would dangle.
				const hasFocusableControl =
					fieldType !== RegEdFieldType.Boolean &&
					fieldType !== RegEdFieldType.Option &&
					fieldType !== RegEdFieldType.MultipleOptions &&
					fieldType !== RegEdFieldType.Date;

				if (fieldType === RegEdFieldType.Label) {
					return (
						<div key={fieldId} className={css(styleSheet.informationalLabel)}>
							{field.label}
							{field.helpText ? ` ${field.helpText}` : ''}
						</div>
					);
				}

				const isInvalid = !!invalidFieldIds?.has(fieldId);
				const lockedNote = fieldType === RegEdFieldType.Date ? lockedFieldNotes?.get(fieldId) : undefined;

				return (
					<div
						key={fieldId}
						className={css(styleSheet.field)}
						data-field-id={fieldId}
						data-invalid={isInvalid || undefined}
					>
						<Label
							styles={[styleSheet.label, isInvalid ? styleSheet.labelInvalid : null]}
							htmlFor={hasFocusableControl ? fieldId : undefined}
						>
							{/* Label lays its children out with flex, so the marker needs to share an item with the
							    question text — on its own it parks to the right of a wrapped question. */}
							<span>
								{field.label}
								{field.isRequired ? <span className={css(styleSheet.requiredMark)}>*</span> : null}
							</span>
						</Label>
						{field.helpText ? <span className={css(styleSheet.helpText)}>{field.helpText}</span> : null}
						{renderWidget(field, fieldType)}
						{lockedNote ? <span className={css(styleSheet.helpText)}>{lockedNote}</span> : null}
						{isInvalid ? <FieldError>This question is required.</FieldError> : null}
					</div>
				);
			})}
		</div>
	);
};
